import React, { useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { Redirect, useHistory } from "react-router-dom"
import { TalkToCarrotLegal } from "./TalkToCarrotLegal"
import { useGetSendMessageDisplay } from "./hooks/useGetSendMessageDisplay"

import { Title } from "#/services/page-title/Title"
import { RootBreadcrumb, createTileElement } from "./util/talkToCarrotMolecules"
import { GuidanceElement } from "#/lib/contentful/components/GuidanceElement"
import { useGetTalkToCarrotCareNavigationGuidance } from "./hooks/useGetTalkToCarrotCareNavigationGuidance"
import { FullScreenLoading } from "#/components/read/shared/FullScreenLoading"
import { useGetTalkToCarrotPhoneSupportDisplay } from "./hooks/useGetTalkToCarrotPhoneSupportDisplay"
import { useGetTalkToCarrotCallUsDisplay } from "./hooks/useGetTalkToCarrotCallUsDisplay"
import TalkToCarrotTilePage from "./TalkToCarrotTilePage"
import { useGetPhoneSupportNumberForUser } from "#/components/dynamic-content/hooks/useQueryPhoneSupport"
import { PageLayout } from "#/features/global-ui/page-layout"
import { PageBreadcrumbHeader } from "#/features/global-ui/page-header"
import { Breadcrumbs, Breadcrumb } from "@carrotfertility/carotene-core"
import { useSendMessagePath } from "./hooks/useSendMessagePath"
import { TwilioVoiceWidget } from "#/components/talk-to-carrot/VoiceWidget/TwilioVoiceWidget"

export const TalkToCarrotCareNavPageRoutes = (): JSX.Element => {
  const history = useHistory()
  const sendMessagePath = useSendMessagePath()

  const intl = useIntl()
  const { locale } = intl

  const { data: sendMessageDisplay, isLoading: isSendMessageDisplayLoading } = useGetSendMessageDisplay(locale)
  const { data: phoneSupportDisplay, isLoading: isPhoneSupportDisplayLoading } =
    useGetTalkToCarrotPhoneSupportDisplay(locale)
  const { data: callUsDisplay, isLoading: isCallUsDisplayLoading } = useGetTalkToCarrotCallUsDisplay(locale)

  const [openFiveNineWidget, setOpenFiveNineWidget] = useState(false)

  const {
    data: phoneSupportNumber,
    isError,
    isInitialLoading: isPhoneSupportNumberLoading
  } = useGetPhoneSupportNumberForUser()
  const { data: guidanceData, isLoading: isLoadingCareNavigationGuidance } =
    useGetTalkToCarrotCareNavigationGuidance(locale)
  const guidance = <GuidanceElement guidance={guidanceData} />

  const sendMessageTile = !isSendMessageDisplayLoading
    ? createTileElement({
        tileDisplay: sendMessageDisplay,
        onClick: () => history.push(sendMessagePath)
      })
    : null

  const shouldDisplayPhoneSupport = !isPhoneSupportDisplayLoading && Boolean(phoneSupportNumber?.e164FormatPhoneNumber)
  const phoneSupportTile = shouldDisplayPhoneSupport
    ? createTileElement({
        tileDisplay: phoneSupportDisplay,
        onClick: () => window.open(`tel:${phoneSupportNumber?.e164FormatPhoneNumber}`, "_self"),
        showArrow: false
      })
    : null

  const callUsTile =
    !isCallUsDisplayLoading && !shouldDisplayPhoneSupport
      ? createTileElement({
          tileDisplay: callUsDisplay,
          onClick: () => setOpenFiveNineWidget(true),
          showArrow: false
        })
      : null

  if (isLoadingCareNavigationGuidance || isPhoneSupportNumberLoading) {
    return <FullScreenLoading />
  }
  if (isError) {
    return <Redirect to="/talk-to-carrot/error" />
  }

  return (
    <PageLayout
      footer={<TalkToCarrotLegal />}
      header={
        <PageBreadcrumbHeader
          breadcrumbs={
            <Breadcrumbs>
              <RootBreadcrumb />
              <Breadcrumb currentPage>
                <FormattedMessage defaultMessage="Care Navigation" />
              </Breadcrumb>
            </Breadcrumbs>
          }
        />
      }
    >
      <Title
        title={intl.formatMessage({ defaultMessage: "Care Navigation" })}
        productTitle={intl.formatMessage({ defaultMessage: "Care Team" })}
      />
      {
        <TalkToCarrotTilePage
          guidance={guidance}
          tiles={[phoneSupportTile, callUsTile, sendMessageTile].filter(Boolean)}
        />
      }
      <TwilioVoiceWidget open={openFiveNineWidget} setOpen={setOpenFiveNineWidget} />
    </PageLayout>
  )
}
